import _swal from "sweetalert2";
import {AxiosResponse} from "axios";
import escapeHtml from "lodash/escape";
import app from "@/main";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {engelleyenUrun, lisansUyariIcerigi} from "@/utils/lisansUyari";
import {urunBilgisi} from "@/utils/urunKatalogu";
import {ENGEL_TAZELEME_ALT_SINIR_MS} from "@/utils/lisansKisit";

declare module "axios" {
    interface AxiosRequestConfig {
        /**
         * Yalnız bu istek için genel durum kodu penceresini bastırılacak kodlar
         * (ör. [404, 409]). Uygulama durumuna dokunmaz; istek bitince kendiliğinden
         * geçerliliğini yitirir. Ayrıntı: StatusCodeHelper.sessizKodlari.
         */
        sessizKodlar?: number[];
    }
}
import {benTazele} from "@/plugins/benTazele";
import {benIstegiMi} from "@/utils/benIstegi";

export class StatusCodeHelper {
    // !!UYARI!! Statik değişken sürekli true OLAMAMALIDIR. Yoksa ilgili uyarı sürekli kapalı kalır.
    static dontShow400Alert: boolean = false;
    static dontShow422Alert: boolean = false;
    static dontShow403Alert: boolean = false;
    static dontShow404Alert: boolean = false;

    success(response: AxiosResponse) {
        let method: string = response.config.method ? response.config.method.toUpperCase() : "";
        if (["POST", "PUT", "DELETE"].includes(method)) {
            app.$toast?.success("İşlem Başarılı");
        }
    }

    /**
     * İstek, uygulamanın kendi origin'i/API'si yerine harici bir mutlak
     * URL'e mi gitti? (ör. https://zirve-bilgisayar.com/cloud/degerlendirme).
     * Göreli URL'ler ve aynı origin daima "iç" sayılır — mevcut 401 davranışı
     * korunur.
     */
    static isExternalRequest(url: any): boolean {
        if (!url || typeof url !== "string") return false;
        if (!/^https?:\/\//i.test(url)) return false; // göreli /api → iç
        try {
            return new URL(url).host !== location.host;
        } catch {
            return false;
        }
    }

    /**
     * [dev-3350] PR #1712 inceleme yorumları 881813313 ve 881813320 — bastırma
     * artık isteğin kendisinde taşınıyor:
     *
     * Statik `dontShowXAlert` bayrakları uygulama genelidir. Bildirim zilinden
     * Excel indirirken ya da export isterken bayrağı kurup `finally`'de indirmek,
     * aynı bayrağı uzun döngülerinde kullanan dört toplu işlem ekranıyla
     * (TopluIslerView, TopluNotAlma, TopluTakipDurumu, TopluTakipTarihi) yarışıyor:
     * indirmenin `finally`'si döngünün bayrağını söndürüyor, döngünün 404'ü ham
     * "404 Sayfa Bulunamadı" penceresi olarak çıkıyordu. Ters yönde de aynı: export
     * isteği sürerken (axios'ta timeout kapalı, `plugins/axios.ts:27`) zilin 60 sn'lik
     * `/bildirim` yoklamasının 500'ü sessizce yutulabiliyordu.
     *
     * Çağrı yeri config'e `sessizKodlar: [404, 409]` yazar; bastırma yalnız o isteğin
     * hatasında geçerlidir, global durum hiç değişmez. 401 (oturum yönlendirmesi)
     * asla bastırılamaz. Kalan `dontShow400/403/404/422Alert` bayrakları toplu
     * işlem ekranları ve formlar tarafından kullanılıyor (toplu ekranlar yalnız
     * 404/403/422); onların istek bazlı yapıya taşınması DEV-3319 kapsamındadır.
     */
    static sessizKodlari(error: any): number[] {
        const kodlar = error && error.config ? error.config.sessizKodlar : undefined;
        return Array.isArray(kodlar) ? kodlar : [];
    }

    /**
     * DEV-2113 — indirme uclari `responseType: 'blob'` ile cagriliyor; hata
     * govdesi de Blob olarak geliyor ve icindeki `type` alani OKUNAMIYOR.
     * Sonuc: rapor/dilekce/XML indirmede lisans 403'u ciplak "yetkiniz yok"
     * olarak cikiyordu -- oysa madde 3, 4 ve 10'un kapattigi isler tam olarak
     * bunlar. Blob once metne cevrilir, sonra normal akis yeniden calisir.
     *
     * Ikinci alarmi engelleyen isaret: helper.download / downloadPost /
     * getBlobFromLink kendi catch bloklarinda ayni Blob'u cozup error()'u
     * TEKRAR cagiriyor. Isaret olmasa kullanici ayni hata icin iki kutu
     * gorurdu (ilki yanlis metinle).
     */
    error(error: any) {
        // DEV-3350 A4: yanıtsız hata (ağ kopması, zaman aşımı) — error.response
        // yoktur. Koşulsuz error.response.status erişimi TypeError atıp
        // interceptor'ı bozuyordu; sessizce dön, mesajı çağrı yeri gösterir.
        if (!error || !error.response) {
            return;
        }
        // Bu durum kodu yalnız BU istek için susturuldu mu? (401 hariç: oturum
        // yönlendirmesi hiçbir istekte bastırılamaz.)
        if (error.response.status !== 401
            && StatusCodeHelper.sessizKodlari(error).indexOf(error.response.status) >= 0) {
            return;
        }
        if (error?.response?.data instanceof Blob) {
            const govde: Blob = error.response.data;
            govde.text()
                .then((metin: string) => {
                    try {
                        error.response.data = JSON.parse(metin);
                    } catch {
                        error.response.data = {};
                    }
                })
                .catch(() => {
                    error.response.data = {};
                })
                .then(() => this.error(error));
            return;
        }

        if (error.__uyariGosterildi) return;
        error.__uyariGosterildi = true;

        switch (error.response.status) {
            case 400 : {
                if (!StatusCodeHelper.dontShow400Alert)
                    this.error400(error);
                break;
            }
            case 401 : {
                // Yalnizca KENDI API'mize (goreli /api veya ayni origin) giden
                // istegin 401'i oturum bitimi sayilir. Harici bir servisin
                // (or. degerlendirme anketi bulut adresi) token'i tanimayip
                // 401 donmesi, kullaniciyi ana uygulamadan atmamalidir.
                if (StatusCodeHelper.isExternalRequest(error?.config?.url)) {
                    break;
                }
                if (!location.href.includes("giris") || (location.href.includes("giris") && app.$store.state.user.id)) {
                    if (location.pathname != '/login')
                        localStorage.setItem('routingPath', location.pathname)
                    localStorage.clear();
                    sessionStorage.clear();
                    location.href = "/giris";
                    console.error("GIRIS HREF");
                }
                break;
            }
            case 402 : {
                this.error402(error)
                break;
            }
            case 403 : {
                // DEV-2113 — lisans kaynakli 403'ler ciplak "yetkiniz yok" olarak
                // gosterilmez: kullanici neyin bittigini ve nasil yenileyecegini
                // gormeli. Ayrim sunucudan gelen 'type' alaniyla yapilir.
                // license_none: hesaba tanimli HIC lisans yok (sunucu tarafinda
                // /ben'in taban kapisi, 12.09.2026 PR incelemesi). Ayri tip cunku
                // mesaj "uzat" degil "paket satin al" dilinde; kutu ayni.
                const lisansTipi = error.response?.data?.type;
                if (lisansTipi === 'license_expired'
                    || lisansTipi === 'license_grace_readonly'
                    || lisansTipi === 'license_none'
                    || lisansTipi === 'feature_disabled') {
                    // DEV-2113 Faz 5 — sunucu ekranin bildiginden farkli karar verdi
                    // (lisans degisti, istisna eklendi ya da bitti): kural ozeti
                    // tazelenir ki dugmeler durumu yakalasin. En fazla 30 saniyede bir;
                    // /ben'in kendi 403'u tazeleme tetiklemez.
                    const benIstegi = benIstegiMi(error?.config?.url);
                    if (!benIstegi) {
                        app.$store.dispatch('ozellikDurumunuTazele', {enAzAralikMs: ENGEL_TAZELEME_ALT_SINIR_MS}).catch(() => undefined);
                    }
                    // Giris donusunde /ben'in lisans 403'unu giris ekrani (LoginCallback)
                    // kendi kartiyla anlatiyor; ustune ayni metinle pencere acilmaz.
                    if (benIstegi && window.location.pathname === '/giris-kontrol') {
                        break;
                    }
                    // dontShow403Alert BILEREK sorulmuyor. O bayrak yetki
                    // kaynakli 403'u tek bir ekranda susturmak icin var ama
                    // hicbir yerde false'a donmuyor (HesapBilgileriView'da bir
                    // kez true yapiliyor). Lisans uyarisi ona baglansaydi,
                    // kullanici hesap bilgilerinde adini bir kez duzenledikten
                    // sonra oturum boyunca HICBIR lisans uyarisi goremezdi --
                    // yani "paket satin al" yonlendirmesi sessizce kaybolurdu.
                    this.errorLisans(error, lisansTipi);
                    break;
                }
                // DEV-2684 Model Y: 403, kullanıcının yetkisinin değişmiş olabileceğine
                // işaret eder; /ben aralık sınırlı tazelenir (menü/buton görünürlüğü
                // düzelir). /ben isteğinin KENDİSİ 403 dönerse tazeleme yine /ben ister →
                // sonsuz döngü; o istek atlanır.
                if (!benIstegiMi(error?.config?.url)) {
                    benTazele();
                }
                if (!StatusCodeHelper.dontShow403Alert)
                    this.error403(error)
                break;
            }
            case 404 : {
                if (!StatusCodeHelper.dontShow404Alert)
                    this.error404();
                break;
            }
            case 409 : {
                // Otomatik sorgu kapısı kapalıyken senkron-plan yazma uçları 409
                // `senkron_kapali` döner (DEV-2603, karar 20.10.2026). Bu bir HATA değil
                // beklenen durumdur: pencere açılmaz, kapı kapalı olarak işaretlenir
                // (menü kalemi kaybolur, zamanlayıcılar durur, plan ekranı kapanır).
                // Başka 409'lar eskisi gibi hata penceresi açar.
                if (error.response.data && error.response.data.type === "senkron_kapali") {
                    this.senkronKapali();
                    break;
                }
                // Sunucu mesajlı 409 (DEV-2684 rol/izin çakışması) mesajıyla gösterilir;
                // mesajsız 409 DEV-3350 gereği default penceresiyle aynı kalır.
                if (error?.response?.data?.message || error?.response?.data?.data?.message)
                    this.error409(error);
                else
                    this.errorDefault(error);
                break;
            }
            case 422 : {
                if (!StatusCodeHelper.dontShow422Alert)
                    this.error422(error);
                break;
            }
            case 429 : {
                this.errorDefault(error);
                break;
            }
            case 500 : {
                this.error500(error);
                break;
            }
            case 503 : {
                this.error503();
                break;
            }
            default : {
                this.errorDefault(error);
                break;
            }
        }
    }

    errorDefault(error: any) {
        let message = "";
        let errors: Array<Array<string>> = error.response.data.errors;
        message = "<h2 style='color: white'>" + error.response.status + "</h2><h4 style='color: white'>" + error.response.statusText + "</h4>";
        if (errors) {
            message += Object.values(errors)
                .map((err: Array<string>) => {
                    return "<ul style='color: white'><li><h4>" + err[0] + "</h4></li></ul>";
                }).join("");
        }
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    /** 409 `senkron_kapali`: sunucunun cevabı bilgidir; kapıyı kapalı işaretler, SESSİZDİR. */
    senkronKapali() {
        app?.$store?.commit("setSenkronAktif", false);
    }

    error400(error: any) {
        let message = "";
        let errors: Array<Array<string>> = error.response.data.errors;
        message = "<h2 style='color: white'>400</h2><h4 style='color: white'>İstek Hatalı</h4>";
        if (errors) {
            message += Object.values(errors)
                .map((err: Array<string>) => {
                    return "<ul style='color: white'><li><h4>" + err[0] + "</h4></li></ul>";
                }).join("");
        }
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    /**
     * DEV-2113 — lisans uyarisi. Dort tip:
     *   license_grace_readonly  kisitli donem: okuma serbest, bu islem kapali
     *   license_expired         o urunun suresi bitti / lisans yok
     *   license_none            hesaba tanimli HIC lisans yok (giris kapisi)
     *   feature_disabled        ozellik bu buroya kapali (herkese kapali, demo)
     *
     * Icerik (baslik, metin, eylem) lisansUyariIcerigi'nden gelir; sunucu mesaji
     * HTML'e konmaz. Engelleyen urun sunucunun `urunler` alanindan secilir.
     * Lisans tiplerinde birincil dugme lisans ekranina goturur: yenileme orada,
     * urun bazinda yapilir (IcraPro/e-IcraPro self-servis, digerleri magaza).
     */
    errorLisans(error: any, tip: string) {
        const veri = error.response?.data ?? {};
        const urunler: number[] = Array.isArray(veri.urunler) ? veri.urunler.map((u: any) => Number(u)) : [];
        const urunDurumlari = app.$store?.getters?.ozellikDurumu?.urun_durumlari ?? {};
        const urun = engelleyenUrun(tip, urunler, urunDurumlari);
        const durum = urun !== null ? (urunDurumlari[String(urun)] ?? null) : null;
        const kalanGun = (urun !== null && tip === 'license_grace_readonly')
            ? (app.$helper?.lisansKisitliKalanGun?.(urun) ?? null)
            : null;
        const icerik = lisansUyariIcerigi(tip, urun, durum, kalanGun);
        // Birincil dugme engelleyen urunun renginde (tema rengi sayfanin urunune gore degisir).
        const urunRengi = urunBilgisi(urun)?.renk ?? null;

        _swal.fire({
            icon: icerik.ikon,
            iconColor: icerik.ikon === 'info' ? '#5BA3E7' : '#F59E0B',
            title: icerik.baslik,
            html: '<p class="lisans-uyari__metin">' + icerik.metin + '</p>'
                + (icerik.not ? '<p class="lisans-uyari__not">' + icerik.not + '</p>' : ''),
            showConfirmButton: icerik.eylem !== null,
            confirmButtonText: icerik.eylem ? icerik.eylem.etiket : undefined,
            showCancelButton: true,
            cancelButtonText: icerik.eylem ? 'Kapat' : 'Tamam',
            reverseButtons: true,
            buttonsStyling: false,
            didOpen: (pencere: HTMLElement) => {
                if (urunRengi) pencere.style.setProperty('--lisans-uyari-renk', urunRengi);
            },
            customClass: {
                popup: 'lisans-uyari',
                title: 'lisans-uyari__baslik',
                htmlContainer: 'lisans-uyari__icerik',
                actions: 'lisans-uyari__eylemler',
                confirmButton: 'lisans-uyari__dugme lisans-uyari__dugme--birincil',
                cancelButton: 'lisans-uyari__dugme lisans-uyari__dugme--ikincil',
            },
        }).then((sonuc: any) => {
            if (sonuc && sonuc.isConfirmed && icerik.eylem) {
                // Ayni sayfadayken yonlendirme hatasi sessiz gecilir.
                app.$router?.push(icerik.eylem.hedef)?.catch?.(() => undefined);
            }
        });
    }

    error402(error: any) {
        _swal.fire({
            icon: 'error',
            title: 'Uyarı',
            html:
                '<b style="font-family: Calibri">' + error.response.data.message + '</b><br><br> ' +
                '<a target="_blank" href="https://zirve-bilgisayar.com/" style="font-family: Tahoma; font-size: medium;">Yükseltme yapmak için tıklayınız..</a> ',
            showCancelButton: true,
            showConfirmButton: false,
            background: "rgba(255,255,255,0.95)",
            cancelButtonText: 'Kapat',
        })
    }

    error403(error: any) {
        // DEV-2684 Model Y: sunucu `sebep: "yalniz_yonetici"` döndürürse kullanıcıya
        // işlemin yalnız Sistem Yöneticisine açık olduğu söylenir; aksi halde genel metin.
        const sebep = error?.response?.data?.sebep;
        const aciklama = sebep === "yalniz_yonetici"
            ? "Bu işlem yalnız Sistem Yöneticisi tarafından yapılabilir."
            : "İşlemi gerçekleştirmeye yetkiniz yoktur.";
        let message = "<h2 style='color: white'>403</h2><h4 style='color: white'>Yasak</h4><h4 style='color: white'>" + aciklama + "</h4>";
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    error404() {
        let message = "<h2 style='color: white'>404</h2><h4 style='color: white'>Sayfa Bulunamadı</h4>";
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    // 409 Çakışma (DEV-2684): kullanımdaki rolü silme (RolCakismasiException) ve eski modda
    // matris yazımı (EskiIzinYazimiKapaliException) sunucudan anlaşılır bir `message` ile döner.
    // Mesaj yoksa genel bir metin gösterilir. Sunucu mesajı veridir (kayıt alanı taşıyabilir):
    // HTML olarak işlenmesin diye kaçışlanır.
    error409(error: any) {
        let mesaj = error?.response?.data?.message
            || error?.response?.data?.data?.message
            || "Bu işlem mevcut durumla çakışıyor ve tamamlanamadı.";
        _swal.fire({
            icon: "warning",
            iconColor: "white",
            position: "top-end",
            html: "<h4 style='color: white'>" + escapeHtml(String(mesaj)) + "</h4>",
            showConfirmButton: false,
            background: "rgba(230,126,34,0.92)",
            timer: 4000,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    error422(error: any) {
        let message = "<h2 style='color: white'>422</h2><h4 style='color: white'>Eksik Veri Girişi</h4><h4 style='color: white'>Lütfen Bu Alanları Doldurun:</h4>";
        let errors: Array<Array<string>> = error.response.data.errors;
        if (errors) {
            message += Object.values(errors)
                .map((err: Array<string>) => {
                    return "<ul style='color: white'><li><h4>" + err[0] + "</h4></li></ul>";
                }).join("");
        }
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }

    error500(error: any) {
        let message;
        if(typeof error.response.data.message != "undefined")
            message = error.response.data.message;
        else
            message = error.config.url + " servisinden anlamlı veri alınamadı !";
        app.$toast.error(message);
    }

    error503() {
        let message = "<h2 style='color: white'>503</h2><h4 style='color: white'>Sunucu Şu Anda Hizmet Vermiyor</h4>";
        _swal.fire({
            icon: "error",
            iconColor: "white",
            position: "top-end",
            html: message,
            showConfirmButton: false,
            background: "rgba(255,0,0,0.5)",
            timer: 3500,
            customClass: {
                container: "errorMessageText",
            },
        });
    }
}